// Book-level performance of a strategy under the EQUAL-SLOT allocation model
// (issue #322). Shared cross-tree: fp_user computes it into the signals payload,
// the strategies widget only renders it.
//
// A strategy with position cap N may hold N positions at once, so every entry is
// sized at 1/N of the book's CURRENT equity. A closed trade with per-trade P&L
// p% therefore moves the whole book by p/N %, and the book compounds trade by
// trade in exit order. Idle slots earn nothing — dividing by N is exactly what
// prices the cash drag of an under-used cap, so a strategy that fires rarely
// reads lower than its per-trade average suggests. That is the point.
//
// The cap's history is NOT recorded (it can be changed at any time), so the
// caller passes the cap in force now and the figure is labelled "at N slots":
// raising the cap retroactively shrinks the displayed return.
//
// Absence is never manufactured into a number: no closed trades → null, not 0.

const finitePct = (v) => v != null && v !== '' && Number.isFinite(Number(v));
const round2 = (v) => Number(v.toFixed(2));

// Compound a sequence of per-trade P&L percentages (in the order given) at 1/cap
// of equity each. Non-numeric entries are skipped; an empty sequence is null.
export function compoundedReturnPct(plPcts, cap) {
  const n = Number(cap);
  if (!Number.isFinite(n) || n <= 0) return null;
  let equity = 1;
  let counted = 0;
  for (const pl of plPcts || []) {
    if (!finitePct(pl)) continue;
    equity *= 1 + Number(pl) / 100 / n;
    counted++;
  }
  return counted ? round2((equity - 1) * 100) : null;
}

// closedPls: per-trade P&L % of CLOSED round-trips in exit-date order.
// openPls:   current mark of each OPEN position (live if newer, else last close).
export function strategyStats({ closedPls = [], openPls = [], cap }) {
  const closed = (closedPls || []).filter(finitePct).map(Number);
  const open = (openPls || []).filter(finitePct).map(Number);
  return {
    trades: closed.length,
    wins: closed.filter((p) => p > 0).length,
    avg_pl_pct: closed.length ? round2(closed.reduce((s, p) => s + p, 0) / closed.length) : null,
    // The record: closed trades only.
    realized_return_pct: compoundedReturnPct(closed, cap),
    // The same walk continued through the open marks. Null when nothing is
    // open, so a renderer shows it only when it can differ from realized.
    incl_open_return_pct: open.length ? compoundedReturnPct([...closed, ...open], cap) : null,
  };
}

// Whole days from a strategy's start to `until` (both 'YYYY-MM-DD…' strings;
// UTC arithmetic, no local-getter round-trip — see the date-only trap in
// CLAUDE.md). Null when either side is missing or unparseable.
export function daysRunning(startedAt, until) {
  const a = Date.parse(String(startedAt || '').slice(0, 10) + 'T00:00:00Z');
  const b = Date.parse(String(until || '').slice(0, 10) + 'T00:00:00Z');
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return Math.round((b - a) / 86400000);
}

// Which price/P&L an open position is marked at: the live quote whenever one is
// newer than the settled bar, else the last close. Price and P&L ALWAYS come
// from the same source, so no surface can mix vintages (issue #138). ONE
// definition for the payload's "incl. open" walk and every card that renders a
// position — reading the stat and toggling a disclosure must agree.
export const positionMark = (pos) => (pos.live_price != null
  ? { isLive: true, price: pos.live_price, pl: pos.live_pl_pct }
  : { isLive: false, price: pos.last_close, pl: pos.unrealized_pl_pct });
